import { useFetch } from "@/lib/shieldx";
import { PageHeader, Panel, Loader, Stat } from "@/components/shieldx/Primitives";
import { DemoTag, RiskBadge } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import { CheckCircle2 } from "lucide-react";

const TONES = ["blue", "emerald", "cyan", "amber", "purple", "red"];
const DEC_TONE = {
  ALLOW: "text-emerald-400", ALLOWED: "text-emerald-400", VERIFIED: "text-emerald-400",
  CHALLENGE: "text-cyan-300", CHALLENGED: "text-cyan-300", "RATE LIMIT": "text-amber-300", "RATE LIMITED": "text-amber-300",
  DELAYED: "text-amber-300", DENY: "text-rose-300", DENIED: "text-rose-300", BLOCK: "text-red-400", BLOCKED: "text-red-400",
  ISOLATE: "text-orange-400", ISOLATED: "text-orange-400",
};

function hhmmss(ts) { try { return new Date(ts).toISOString().substr(11, 8); } catch { return "--:--:--"; } }

export default function ModuleView({ name, icon, children }) {
  const { data, loading } = useFetch(`/modules/${name}/overview`);
  if (loading || !data) return <Loader label="Loading module" />;
  const metrics = Object.entries(data.metrics || {});
  return (
    <div>
      <PageHeader icon={icon} title={data.title} subtitle={data.subtitle}
        actions={<div className="flex items-center gap-2"><span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 sx-pulse" />{data.status}</span><DemoTag /></div>} />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {metrics.map(([k, v], i) => <Stat key={k} label={k} value={v} tone={TONES[i % TONES.length]} />)}
      </div>

      {children}

      {(data.panels || []).map((p, i) => (
        <Panel key={i} title={p.title} className="mt-4" testid={`module-panel-${i}`}>
          <ol className="relative space-y-2.5 border-l border-slate-800 pl-5">
            {p.steps.map((s, j) => (
              <li key={j} className="relative text-sm text-slate-300">
                <span className="absolute -left-[22px] top-1 h-2 w-2 rounded-full bg-blue-500" />{s}
              </li>
            ))}
          </ol>
        </Panel>
      ))}

      <Panel title="Live Activity" desc="Sanitized, evidence-based — SIMULATED/DEMO" className="mt-4" testid="module-stream">
        <Table testid="module-stream-table" empty="No activity"
          columns={[
            { key: "ts", label: "Time", render: (r) => <span className="font-mono text-[11px] text-slate-500">{hhmmss(r.ts)}</span> },
            { key: "label", label: "Event", render: (r) => <span className="text-slate-200">{r.label}</span> },
            { key: "asset", label: "Asset", render: (r) => <span className="font-mono text-[11px] text-slate-400">{r.asset}</span> },
            { key: "source", label: "Source", render: (r) => <span className="text-slate-400">{r.source}</span> },
            { key: "severity", label: "Severity", render: (r) => <RiskBadge value={r.severity} /> },
            { key: "decision", label: "Decision", render: (r) => <span className={`font-mono text-[11px] font-semibold ${DEC_TONE[r.decision] || "text-slate-300"}`}>{r.decision}</span> },
            { key: "incident", label: "Incident", render: (r) => <span className="font-mono text-[10px] text-slate-500">{r.incident || "—"}</span> },
          ]}
          rows={data.stream || []} />
      </Panel>
    </div>
  );
}
